import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Zap, Sparkles, Crown, CreditCard, LogOut } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import RegisterModal from '../marketing/RegisterModal';
import UpgradeModal from './UpgradeModal';

export default function PlatformNav({ issueLabel = 'Issue #104 · May 19', onSearch }) {
  const { user, entitlement, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [upgradePlan, setUpgradePlan] = useState(null);  // free→pro only
  const [showPremiumUpgrade, setShowPremiumUpgrade] = useState(false);
  const wrapRef = useRef(null);

  const initials = user?.email?.[0]?.toUpperCase() ?? 'U';
  const isPro = entitlement !== 'free';

  useEffect(() => {
    if (!menuOpen) return;
    function onClickOutside(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setMenuOpen(false);
    }
    function onEsc(e) { if (e.key === 'Escape') setMenuOpen(false); }
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onEsc);
    };
  }, [menuOpen]);

  function handleUpgradePro() {
    setMenuOpen(false);
    setUpgradePlan('monthly');
  }

  function handleUpgradePremium() {
    setMenuOpen(false);
    setShowPremiumUpgrade(true);
  }

  function handleBilling() {
    setMenuOpen(false);
    navigate('/billing');
  }

  function handleLogout() {
    setMenuOpen(false);
    logout();
  }

  return (
    <>
    {/* Free → Pro: existing Stripe Checkout flow */}
    {upgradePlan && entitlement === 'free' && (
      <RegisterModal
        initialStep="payment"
        initialPlan={upgradePlan}
        initialUser={user}
        onClose={() => setUpgradePlan(null)}
      />
    )}
    {/* Pro → Premium: in-place upgrade with proration */}
    {showPremiumUpgrade && (
      <UpgradeModal toTier="premium" onClose={() => setShowPremiumUpgrade(false)} />
    )}
    <nav className="nav">
      <div className="nav-in">
        <div className="nav-brand" onClick={() => navigate('/platform')} style={{ cursor: 'pointer' }}>
          <div className="nav-brand-dot" /> MIG
          {entitlement === 'pro' && <span className="nav-tier-pill nav-tier-pill--pro"><Sparkles size={9} />Pro</span>}
          {entitlement === 'premium' && <span className="nav-tier-pill nav-tier-pill--premium"><Crown size={9} />Premium</span>}
        </div>
        <div className="nav-search">
          <Search size={14} style={{ color: 'var(--muted)', flexShrink: 0 }} />
          <input
            type="text"
            placeholder="Search stocks, industries, signals…"
            onChange={e => onSearch?.(e.target.value)}
          />
          <span className="nav-search-kb">⌘K</span>
        </div>
        <div className="nav-platform-right">
          <div className="nav-issue">
            <span className="live-dot" />
            {issueLabel}
          </div>
          {!isPro && (
            <button className="nav-upgrade" onClick={() => setUpgradePlan('monthly')}>
              <Zap size={12} /> Upgrade to Pro · $14.95
            </button>
          )}
          <div className="nav-avatar-wrap" ref={wrapRef}>
            <div className="nav-avatar" onClick={() => setMenuOpen(o => !o)} title={user?.email}>
              {initials}
            </div>

            {menuOpen && (
              <div className="nav-avatar-menu">
                <div className="nav-avatar-plan">
                  <div className="nav-avatar-plan-label">Signed in as</div>
                  <div className="nav-avatar-plan-email">{user?.email}</div>
                  <div className={`nav-avatar-plan-badge${isPro ? '' : ' free'}`}>
                    {entitlement === 'premium' ? <><Crown size={11} /> Premium</> : entitlement === 'pro' ? <><Sparkles size={11} /> Pro</> : 'Free'}
                  </div>
                </div>
                <div className="nav-avatar-items">
                  {entitlement === 'free' && (
                    <button className="nav-avatar-item upgrade-pro" onClick={handleUpgradePro}>
                      <Sparkles size={14} /> Upgrade to Pro
                    </button>
                  )}
                  {entitlement === 'pro' && (
                    <button className="nav-avatar-item upgrade-premium" onClick={handleUpgradePremium}>
                      <Crown size={14} /> Upgrade to Premium
                    </button>
                  )}
                  <button className="nav-avatar-item" onClick={handleBilling}>
                    <CreditCard size={14} /> Billing
                  </button>
                  <button className="nav-avatar-item danger" onClick={handleLogout}>
                    <LogOut size={14} /> Log out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
    </>
  );
}
